import { FileText } from "lucide-react"

import { Avatar } from "../Avatar"
import { cn } from "@/lib/utils"

// A file chip under a message (DOCX/MP3/etc.). The little extension tile mirrors
// the prototype's `.att .ex`.
function FileChip({ ext, name, meta }) {
  return (
    <div className="mt-[9px] flex items-center gap-[9px] rounded-[9px] border border-border bg-surface3 px-[11px] py-[9px]">
      <span className="grid size-[30px] shrink-0 place-items-center rounded-[7px] bg-muted-foreground font-display text-[8px] font-bold text-card">
        {ext || <FileText className="size-4" strokeWidth={1.8} />}
      </span>
      <div className="min-w-0">
        <div className="truncate text-[12px] font-semibold">{name}</div>
        {meta && <div className="text-[10.5px] text-ink-3">{meta}</div>}
      </div>
    </div>
  )
}

// Striped image-attachment placeholders (the real files render lazily elsewhere;
// in the thread they read as labelled tiles, matching the prototype).
function ImageAttachments({ labels }) {
  return (
    <div className="mt-[9px] flex gap-2">
      {labels.map((label, i) => (
        <div
          key={`${label}-${i}`}
          className="flex h-[72px] w-[96px] items-end rounded-[9px] border border-border bg-[image:repeating-linear-gradient(45deg,var(--secondary),var(--secondary)_7px,var(--accent)_7px,var(--accent)_14px)] p-1.5"
        >
          <span className="font-display text-[8.5px] font-semibold text-ink-3">{label}</span>
        </div>
      ))}
    </div>
  )
}

const URL_PATTERN = /(https?:\/\/[^\s]+)/g

// Render customer-entered links as real anchors without treating the message as
// HTML. Splitting text keeps React's escaping in place, so linkification cannot
// turn message content into executable markup.
function LinkifiedText({ children }) {
  return String(children ?? "")
    .split(URL_PATTERN)
    .map((part, index) =>
      /^https?:\/\/[^\s]+$/.test(part) ? (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-current underline decoration-current/50 underline-offset-2 hover:decoration-current"
        >
          {part}
        </a>
      ) : (
        part
      )
    )
}

// One message in a thread — the single bubble vocabulary shared by the order
// Chat tab and the Messages inbox (design CHANGELOG Stage 5: extract on the 3rd
// appearance). The sender ("me") bubble uses the calm blue `--bubble` token, NOT
// brand-red (red is reserved for CTA/destructive). Incoming bubbles carry the
// sender avatar + name·role; outgoing right-align with no name.
//
// Optimistic send (shared with useOptimisticSend): a `pending` bubble is dimmed
// with "Sending…" in the timestamp slot; an `error` renders a small inline row
// under the bubble with the message and Retry / Edit / Dismiss per the failure
// kind (Retry when `error.canRetry`, Edit for `flagged_content`, Dismiss always).
export default function ChatBubble({
  mine = false,
  who,
  role,
  at,
  body,
  files = [],
  images = [],
  pending = false,
  error = null,
  onRetry,
  onEdit,
  onDismiss,
}) {
  return (
    <div className={cn("flex max-w-[76%] gap-2.5 max-[721px]:max-w-[88%]", mine && "ml-auto flex-row-reverse")}>
      {!mine && <Avatar initials={who?.slice(0, 2).toUpperCase()} name={who} size={32} />}

      <div className={cn("min-w-0", mine && "text-right")}>
        {!mine && who && (
          <div className="mb-1 text-[11.5px] font-bold text-muted-foreground">
            {who}
            {role && <span className="ml-1.5 font-medium text-ink-3">· {role}</span>}
          </div>
        )}

        <div
          className={cn(
            "border px-3.5 py-[11px] text-left text-[13px] leading-[1.5] break-words whitespace-pre-wrap [text-wrap:pretty]",
            mine
              ? "rounded-[14px_5px_14px_14px] border-bubble bg-bubble text-bubble-foreground"
              : "rounded-[5px_14px_14px_14px] border-border bg-card text-foreground",
            pending && "opacity-60"
          )}
        >
          <LinkifiedText>{body}</LinkifiedText>
          {files.map((f, i) => (
            <FileChip key={`f-${i}`} ext={f.ext} name={f.name} meta={f.meta} />
          ))}
          {images.length > 0 && <ImageAttachments labels={images} />}
        </div>

        {error ? (
          <div className={cn("mt-1.5 flex flex-col gap-1.5", mine ? "items-end text-right" : "items-start")}>
            <p className="max-w-[280px] text-[11px] leading-[1.45] text-primary">{error.text}</p>
            <div className={cn("flex items-center gap-3 text-[11px] font-semibold", mine && "flex-row-reverse")}>
              {error.canRetry && (
                <button type="button" onClick={onRetry} className="text-primary hover:underline">
                  Retry
                </button>
              )}
              {error.type === "flagged_content" && (
                <button type="button" onClick={onEdit} className="text-muted-foreground hover:underline">
                  Edit
                </button>
              )}
              <button type="button" onClick={onDismiss} className="text-ink-3 hover:underline">
                Dismiss
              </button>
            </div>
          </div>
        ) : (
          (at || pending) && (
            <div className={cn("mt-1.5 text-[10.5px] text-ink-3", mine && "text-right")}>
              {pending ? "Sending…" : at}
            </div>
          )
        )}
      </div>
    </div>
  )
}
